// src/composables/useProductForm.ts
import { ref, computed } from 'vue';
import api from '@/services/api';
import { useRouter } from 'vue-router';
import categoryService from '@/services/categoryService';
import securityService from '@/services/security';

// Interface za kategoriju
interface Category {
  id: string;
  name: string;
  parent?: {
    id: string;
    name: string;
  };
}

// Interface za sliku proizvoda
interface ProductImage {
  id?: string;
  path?: string;
  order: number;
  isMain: boolean;
  file?: File;
  preview?: string;
  rotation?: number;
}

// Interface za formu proizvoda
interface ProductFormData {
  name: string;
  description: string;
  price: string;
  categoryId: string;
  images: ProductImage[];
}

// Interface za statistike korisnika
interface UserStats {
  activeProductsCount: number;
  totalProductsCount: number;
  maxProductsAllowed: number;
  remainingSlots: number;
  inactiveProductsCount: number;
}

// Interface za odgovor s proizvoda
interface ProductResponse {
  status: string;
  message: string;
  data: {
    id: string;
    name: string;
    slug: string;
    description: string;
    price: string;
    stock: number;
    isActive: boolean;
    userId: string;
    pictures: {
      id: string;
      path: string;
      order: number;
      isMain: boolean;
    }[];
    mainPicture: string;
    category: Category;
    createdAt: string;
    updatedAt: string;
    expiresAt: string;
    renewedAt: string | null;
  };
}

export function useProductForm() {
  const router = useRouter();
  const formData = ref<ProductFormData>({
    name: '',
    description: '',
    price: '',
    categoryId: '',
    images: [],
  });

  // Za praćenje stanja
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const successMessage = ref<string | null>(null);
  const categories = ref<Category[]>([]);

  const userStats = ref<UserStats>({
    activeProductsCount: 0,
    totalProductsCount: 0,
    maxProductsAllowed: 100,
    remainingSlots: 100,
    inactiveProductsCount: 0,
  });
  const isLoadingStats = ref(false);

  // Toast notification state
  const toast = ref({
    show: false,
    message: '',
    type: 'info' as 'success' | 'error' | 'info' | 'warning',
  });

  const canAddProduct = computed(() => {
    return userStats.value.remainingSlots > 0;
  });

  const isAtLimit = computed(() => {
    return (
      userStats.value.activeProductsCount >= userStats.value.maxProductsAllowed
    );
  });

  const showToast = (
    message: string,
    type: 'success' | 'error' | 'info' | 'warning' = 'info'
  ) => {
    // Sanitizacija poruke koja se prikazuje korisniku
    const sanitizedMessage = securityService.sanitizeInput(message);

    toast.value = {
      show: true,
      message: sanitizedMessage,
      type,
    };

    // Auto-hide toast after 3 seconds
    setTimeout(() => {
      toast.value.show = false;
    }, 3000);
  };

  const loadUserStats = async () => {
    isLoadingStats.value = true;
    try {
      const response = await api.get('/api/products/statistics');
      if (response.data && response.data.status === 'success') {
        userStats.value = response.data.data;
        console.log('User stats loaded:', userStats.value);
      }
    } catch (err) {
      console.error('Greška pri učitavanju statistika korisnika:', err);
      // Fallback na default vrednosti ako ne može da učita
      userStats.value = {
        activeProductsCount: 0,
        totalProductsCount: 0,
        maxProductsAllowed: 100,
        remainingSlots: 100,
        inactiveProductsCount: 0,
      };
    } finally {
      isLoadingStats.value = false;
    }
  };

  // Učitavanje kategorija
  const loadCategories = async () => {
    try {
      categories.value = await categoryService.getCategories();

      if (categories.value.length === 0) {
        error.value =
          'Nije moguće učitati kategorije. Pokušajte ponovo kasnije.';
      }
    } catch (err) {
      console.error('Greška pri učitavanju kategorija:', err);
      error.value = 'Nije moguće učitati kategorije. Pokušajte ponovo kasnije.';
    }
  };

  // Dodavanje slike
  const addImage = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const newImage: ProductImage = {
        order: formData.value.images.length + 1,
        isMain: formData.value.images.length === 0, // Za UI - prva slika je glavna
        file,
        preview: e.target?.result as string,
        rotation: 0,
      };
      formData.value.images.push(newImage);

      console.log(
        'Slike nakon dodavanja:',
        formData.value.images.map((img) => ({
          order: img.order,
          isMain: img.isMain,
          fileName: img.file?.name || 'Unknown',
        }))
      );
    };
    reader.readAsDataURL(file);
  };

  // Brisanje slike
  const removeImage = (index: number) => {
    // Brišemo sliku
    formData.value.images.splice(index, 1);

    // Ažuriramo order za sve slike
    formData.value.images.forEach((img, idx) => {
      img.order = idx + 1;
      // Backend ne koristi isMain, ali ćemo ga ažurirati za UI
      img.isMain = idx === 0;
    });

    console.log(
      'Slike nakon brisanja:',
      formData.value.images.map((img) => ({
        order: img.order,
        isMain: img.isMain,
        fileName: img.file?.name || 'Unknown',
      }))
    );
  };

  // Postavljanje glavne slike
  const setMainImage = (index: number) => {
    // Pronađemo sliku koju želimo postaviti kao glavnu
    const imageToSetAsMain = formData.value.images[index];

    if (!imageToSetAsMain) return;

    // Uklonimo tu sliku iz niza
    formData.value.images.splice(index, 1);

    // Postavimo je kao prvu u nizu
    formData.value.images.unshift(imageToSetAsMain);

    // Ažuriramo order za sve slike
    formData.value.images.forEach((img, idx) => {
      img.order = idx + 1;
      // Backend ne koristi isMain, ali ćemo ga ažurirati za UI
      img.isMain = idx === 0;
    });

    console.log(
      'Slike nakon postavljanja glavne:',
      formData.value.images.map((img) => ({
        order: img.order,
        isMain: img.isMain,
        fileName: img.file?.name || 'Unknown',
      }))
    );
  };

  // Rotiranje slike
  const rotateImage = (index: number) => {
    const image = formData.value.images[index];
    const currentRotation = image.rotation || 0;
    const newRotation = (currentRotation + 90) % 360;

    image.rotation = newRotation;
  };

  // Funkcija koja rotira sliku i vraća novi File objekat
  const applyRotationToImage = (file: File, degrees: number): Promise<File> => {
    return new Promise((resolve, reject) => {
      if (degrees === 0) {
        resolve(file); // Nema rotacije, vrati original
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        if (!event.target?.result) {
          reject(new Error('Failed to read file'));
          return;
        }

        const img = new Image();
        img.onload = () => {
          // Kreiramo canvas za rotaciju
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          // Računamo novu širinu i visinu ako je rotacija 90 ili 270 stepeni
          let width = img.width;
          let height = img.height;

          if (degrees === 90 || degrees === 270) {
            // Zamena širine i visine
            width = img.height;
            height = img.width;
          }

          // Postavljamo dimenzije canvas-a
          canvas.width = width;
          canvas.height = height;

          // Transliramo i rotiramo kontekst
          ctx.translate(width / 2, height / 2);
          ctx.rotate((degrees * Math.PI) / 180);

          // Crtamo rotiranu sliku
          ctx.drawImage(img, -img.width / 2, -img.height / 2);

          // Konvertujemo canvas u blob
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob from canvas'));
              return;
            }

            // Kreiramo novi File objekat sa rotiranom slikom
            const rotatedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: file.lastModified,
            });

            resolve(rotatedFile);
          }, file.type);
        };

        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };

        // Postavimo src slike na učitani fajl
        img.src = event.target.result as string;
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      // Čitamo fajl kao Data URL
      reader.readAsDataURL(file);
    });
  };

  const createProduct = async () => {
    if (!canAddProduct.value) {
      const errorMessage = `Dostigli ste maksimalan broj aktivnih oglasa (${userStats.value.maxProductsAllowed}). Ne možete dodati novi oglas.`;
      error.value = errorMessage;
      showToast(errorMessage, 'error');
      return;
    }

    isLoading.value = true;
    error.value = null;
    successMessage.value = null;

    try {
      // Validacija forme pre slanja
      const validation = await validateForm();
      if (!validation.isValid) {
        const firstError = Object.values(validation.errors)[0];
        error.value = firstError;
        showToast(firstError, 'error');
        isLoading.value = false;
        return;
      }

      // Kreiranje FormData objekta za slanje fajlova
      const submitData = new FormData();

      // Sanitizacija podataka pre slanja
      const sanitizedName = securityService.sanitizeInput(formData.value.name);
      const sanitizedDescription = securityService.sanitizeInput(
        formData.value.description
      );

      submitData.append('name', sanitizedName);
      submitData.append('description', sanitizedDescription);
      submitData.append('price', formData.value.price);
      submitData.append('categoryId', formData.value.categoryId);

      // Dodavanje slika - samo ako postoje
      if (formData.value.images.length > 0) {
        // Koristimo map/Promise.all za paralelno procesiranje svih slika
        const processedImages = await Promise.all(
          formData.value.images.map(async (image) => {
            if (!image.file) return null;

            // Primenimo rotaciju ako postoji
            const rotation = image.rotation || 0;
            const processedFile =
              rotation !== 0
                ? await applyRotationToImage(image.file, rotation)
                : image.file;

            return {
              ...image,
              file: processedFile,
            };
          })
        );

        // Filtriramo eventualne null vrednosti i dodajemo u FormData
        processedImages
          .filter((img) => img !== null)
          .forEach((image, index) => {
            if (image && image.file) {
              submitData.append('images', image.file);
              submitData.append(`imageOrder_${index}`, image.order.toString());
              submitData.append(
                `imageIsMain_${index}`,
                image.isMain ? 'true' : 'false'
              );
            }
          });
      } else {
        console.log('Kreiranje oglasa bez slika.');
      }

      // Debug log za pregled FormData sadržaja
      console.log('FormData sadržaj:');
      for (const pair of submitData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }

      // Koristimo api.post direktno
      const response = await api.post('/api/products', submitData);

      if (response.data && response.data.status === 'success') {
        userStats.value.activeProductsCount += 1;
        userStats.value.totalProductsCount += 1;
        userStats.value.remainingSlots = Math.max(
          0,
          userStats.value.maxProductsAllowed -
            userStats.value.activeProductsCount
        );

        // Sanitizacija poruke pre prikazivanja
        const sanitizedSuccess = 'Proizvod je uspešno kreiran!';
        successMessage.value = sanitizedSuccess;
        showToast(sanitizedSuccess, 'success');

        console.log('Product created successfully:', response.data.data);

        // Redirekcija na stranicu proizvoda koristeći slug
        setTimeout(() => {
          router.push(`/product/${response.data.data.slug}`);
        }, 1500);
      }
    } catch (err: any) {
      console.error('Greška pri kreiranju proizvoda:', err);

      if (err.response) {
        console.error('Server response:', {
          status: err.response.status,
          data: err.response.data,
          headers: err.response.headers,
        });
      }

      let errorMessage =
        'Došlo je do greške pri kreiranju proizvoda. Pokušajte ponovo.';

      if (err.response && err.response.data && err.response.data.message) {
        // Sanitizacija poruke o grešci sa servera
        errorMessage = securityService.sanitizeInput(err.response.data.message);
      }

      error.value = errorMessage;
      showToast(errorMessage, 'error');
    } finally {
      isLoading.value = false;
    }
  };

  // Validacija forme
  const validateForm = async (): Promise<{
    isValid: boolean;
    errors: Record<string, string>;
  }> => {
    const errors: Record<string, string> = {};

    // Prvo sanitizujemo podatke pre validacije
    const sanitizedName = securityService.sanitizeInput(formData.value.name);
    const sanitizedDescription = securityService.sanitizeInput(
      formData.value.description
    );

    if (!sanitizedName.trim()) {
      errors.name = 'Naziv proizvoda je obavezan';
    } else if (sanitizedName.length < 3) {
      errors.name = 'Naziv proizvoda mora imati najmanje 3 karaktera';
    } else if (sanitizedName.length > 100) {
      errors.name = 'Naziv proizvoda ne sme biti duži od 100 karaktera';
    }

    if (!sanitizedDescription.trim()) {
      errors.description = 'Opis proizvoda je obavezan';
    } else if (sanitizedDescription.length < 10) {
      errors.description = 'Opis proizvoda mora imati najmanje 10 karaktera';
    }

    if (!formData.value.price.trim()) {
      errors.price = 'Cena je obavezna';
    } else if (
      isNaN(parseFloat(formData.value.price)) ||
      parseFloat(formData.value.price) <= 0
    ) {
      errors.price = 'Cena mora biti pozitivan broj';
    }

    if (!formData.value.categoryId) {
      errors.categoryId = 'Kategorija je obavezna';
    } else {
      // Dodatna validacija kategorije - mora biti podkategorija
      try {
        const isSubcategory = await categoryService.isSubcategory(
          formData.value.categoryId
        );
        if (!isSubcategory) {
          errors.categoryId = 'Morate izabrati podkategoriju';
        }
      } catch (err) {
        console.error('Greška pri proveri kategorije:', err);
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  return {
    formData,
    isLoading,
    error,
    successMessage,
    categories,
    toast,
    loadCategories,
    addImage,
    removeImage,
    setMainImage,
    rotateImage,
    createProduct,
    validateForm,
    showToast,
    userStats,
    isLoadingStats,
    canAddProduct,
    isAtLimit,
    loadUserStats,
  };
}
